// import type { InteractionItem } from 'chart.js'; // -> used by typescript see
//https://github.com/reactchartjs/react-chartjs-2/blob/master/sandboxes/chart/events/App.tsx
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    LinearScale,
    Tooltip,
} from "chart.js";
import Placeholder from "components/Layout/Placeholder";
import Widget from "components/Layout/Widget";
import { ExecutionStatus } from "config/ts-utils";
import { executionTypes } from "containers/ExecutionFilter/constants";
import { getSingleStatFetching } from "containers/Stats/Stats.selectors";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useRef } from "react";
import { Chart, getElementAtEvent } from "react-chartjs-2";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { buildExecutionFilterPathForResource } from "containers/ExecutionFilter/filterStorage";
import { getFilterString } from "utils/utils";
import "./OverTimeChart.scss";

const OverTimeChart = ({ config, resourceData, stats }) => {
    const history = useHistory();
    ChartJS.register(LinearScale, CategoryScale, BarElement, BarController, Tooltip);
    const chartRef = useRef(null);
    const days = [];
    for (let i = 6; i >= 0; i -= 1) {
        const day = dayjs().subtract(i, "days");
        days.push(day.format("ddd"));
    }

    const statDataFetching = useSelector(getSingleStatFetching);

    if (statDataFetching?.includes(config?.selector)) {
        return <Placeholder.Widget size={230} />;
    }
    const data = config?.dto({ resourceData, stats });

    const getDateFromDayIndex = (dayindex) => {
        // we have a chart which goes 7 days to the past with index 0...6
        // index 0 is 7 days ago
        let d = new Date();
        if (dayindex !== 6) {
            d.setDate(d.getDate() - (6 - dayindex));
        }
        return d;
    };

    const getStatusFilter = (backgroundColor) => {
        const successColor = data?.find((e) => e.label === "Successful")?.backgroundColor;

        const filter = {
            field: "status",
            op: "in",
            value:
                backgroundColor?.toLowerCase() === successColor?.toLowerCase()
                    ? [ExecutionStatus.ENDED_SUCCESS]
                    : [ExecutionStatus.ENDED_ERROR, ExecutionStatus.ENDED_CANCELLED],
        };

        return filter;
    };

    const getLocalISOString = (date) => {
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
    };

    const onClick = (event) => {
        const { current: chart } = chartRef;
        if (!chart) {
            return;
        }
        const element = getElementAtEvent(chart, event);
        if (element.length > 0) {
            const dateAfter = getDateFromDayIndex(element[0].index);
            const dateBefore = new Date(dateAfter);
            dateAfter.setHours(0);
            dateAfter.setMinutes(0);
            dateBefore.setHours(23);
            dateBefore.setMinutes(59);
            const bgColor = element[0].element.options.$animations.backgroundColor._from;
            const location = resourceData?.resource_type?.toLowerCase();
            const locationField =
                location === "project" || location === "bundle"
                    ? [
                          {
                              field: `${location}_id`,
                              op: "in",
                              value: [resourceData?.id],
                          },
                      ]
                    : [];
            const filters = [
                getStatusFilter(bgColor),
                {
                    field: "created_at",
                    op: "gt",
                    value: getLocalISOString(dateAfter).slice(0, -8),
                },
                {
                    field: "created_at",
                    op: "lt",
                    value: getLocalISOString(dateBefore).slice(0, -8),
                },
                {
                    field: "execution_type",
                    op: "in",
                    value: executionTypes,
                },
                ...locationField,
            ];
            history.push(
                buildExecutionFilterPathForResource(resourceData, {
                    filter: getFilterString(filters),
                })
            );
        }
    };

    if (!data) return <Widget.Empty />;

    return (
        <div
            className="chart-container"
            style={{ position: "relative", height: "230px", width: "100%" }}
        >
            <Chart
                type="bar"
                options={{
                    legend: { display: false },
                    maintainAspectRatio: false,
                    scales: {
                        xAxis: {
                            grid: {
                                display: false,
                                color: "#fafafa",
                            },
                        },
                        yAxis: {
                            ticks: {
                                // maxTicksLimit: 3,
                                color: "#7e7e7e",
                                font: {
                                    size: 14,
                                    family: "DM Sans, sans-serif",
                                },
                            },
                            grid: {
                                color: "#fafafa",
                            },
                        },
                    },
                    onHover: (event, chartElement) => {
                        if (chartElement) {
                            event.native.target.style.cursor = chartElement[0]
                                ? "pointer"
                                : "default";
                        }
                    },
                }}
                data={{
                    labels: days,
                    datasets: data,
                }}
                ref={chartRef}
                onClick={onClick}
            />
        </div>
    );
};

OverTimeChart.defaultProps = {
    resourceData: {},
    stats: {},
};

OverTimeChart.propTypes = {
    config: PropTypes.oneOfType([PropTypes.object]).isRequired,
    resourceData: PropTypes.oneOfType([PropTypes.object]),
    stats: PropTypes.oneOfType([PropTypes.object]),
};

export default OverTimeChart;
